import React, { useEffect, useMemo, useRef, useState } from "react";
import { Helmet } from "react-helmet-async";
import { HreflangTags } from "../components/SeoHreflang";
import {
  Box,
  Container,
  Typography,
  Avatar,
  Grid,
  Paper,
  TextField,
  Button,
  Stack,
  Divider,
  Chip,
  IconButton,
  Alert,
  CircularProgress,
  LinearProgress,
} from "@mui/material";
import {
  Edit as EditIcon,
  Timeline as StatsIcon,
  Favorite as FavoriteIcon,
  History as HistoryIcon,
  CreditCard as PaymentIcon,
  Lock as LockIcon,
  CameraAlt as CameraIcon,
  Logout as LogoutIcon,
  CheckCircleOutline,
  Close as CloseIcon,
  SaveOutlined,
  CalendarMonth,
  Person as PersonIcon,
} from "@mui/icons-material";
import { useAuth } from "../contexts/AuthContext";
import { Navigate, useNavigate } from "react-router-dom";
import { MEMBERSHIP_COLORS } from "../components/membership/MembershipStyles";
import { apiClient, authService } from "../services/authService";
import { CopyButton, DOMAIN } from "./admin/marketingShared";

interface ProfileFormData {
  name: string;
  email: string;
  phone: string;
  bio: string;
}

interface ActiveSubscription {
  _id: string;
  plan: string;
  billingCycle: string;
  status: string;
  price: number;
  currency?: string;
  endDate?: string;
  startDate?: string;
  paymentMethod?: string;
}

interface SubscriptionResponse {
  success: boolean;
  data: ActiveSubscription | null;
  hasActiveSubscription: boolean;
}

interface FavoritesCountResponse {
  success: boolean;
  data: { count: number };
}

interface PurchasesResponse {
  success: boolean;
  data: unknown[];
}

const NAVY  = "#0A1628";
const ACCENT = "#1565C0";

const Profile: React.FC = () => {
  const { user, logout, updateUser, isAuthReady } = useAuth();
  const navigate = useNavigate();

  const [editMode, setEditMode] = useState(false);
  const [activeData, setActiveData] = useState<ProfileFormData>({ name: "", email: "", phone: "", bio: "" });
  const [profileLoading, setProfileLoading] = useState(true);
  const [saveLoading, setSaveLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saveMessage, setSaveMessage] = useState<string | null>(null);
  const [favoritesCount, setFavoritesCount] = useState(0);
  const [purchasesCount, setPurchasesCount] = useState(0);
  const [lastLoginAt, setLastLoginAt] = useState<string | undefined>(undefined);
  const [subscription, setSubscription] = useState<ActiveSubscription | null>(null);
  const [photoUploading, setPhotoUploading] = useState(false);
  const [photoError, setPhotoError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!user) return;
    let isMounted = true;
    setActiveData({ name: user.name || "", email: user.email || "", phone: user.phone || "", bio: user.bio || "" });
    setLastLoginAt(user.lastLoginAt);

    const loadAncillaryData = async () => {
      setProfileLoading(true);
      setError(null);
      const [subscriptionResult, favoritesResult, purchasesResult] = await Promise.allSettled([
        apiClient.get<SubscriptionResponse>("/subscriptions/me"),
        apiClient.get<FavoritesCountResponse>("/favorites/count"),
        apiClient.get<PurchasesResponse>("/purchases/me"),
      ]);
      if (!isMounted) return;
      if (subscriptionResult.status === "fulfilled") setSubscription(subscriptionResult.value.data.data || null);
      if (favoritesResult.status === "fulfilled") setFavoritesCount(favoritesResult.value.data.data?.count || 0);
      if (purchasesResult.status === "fulfilled") setPurchasesCount(Array.isArray(purchasesResult.value.data.data) ? purchasesResult.value.data.data.length : 0);
      setProfileLoading(false);
    };

    loadAncillaryData();
    return () => { isMounted = false; };
  }, [user]);

  const handlePhotoChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      setPhotoUploading(true);
      setPhotoError(null);
      const avatarUrl = await authService.uploadPhoto(file);
      updateUser({ avatar: avatarUrl });
    } catch {
      setPhotoError("Photo upload failed. Please try again.");
    } finally {
      setPhotoUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const formattedLastLogin = useMemo(() => {
    if (!lastLoginAt) return "No data";
    const date = new Date(lastLoginAt);
    if (Number.isNaN(date.getTime())) return "No data";
    return new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric", year: "numeric" }).format(date);
  }, [lastLoginAt]);

  const subscriptionPrice = useMemo(() => {
    if (!subscription) return null;
    return new Intl.NumberFormat("en-US", { style: "currency", currency: subscription.currency || "USD", minimumFractionDigits: 2 }).format(subscription.price || 0);
  }, [subscription]);

  const nextBillingDate = useMemo(() => {
    if (!subscription?.endDate) return "No data";
    const date = new Date(subscription.endDate);
    if (Number.isNaN(date.getTime())) return "No data";
    return new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric", year: "numeric" }).format(date);
  }, [subscription]);

  // Billing cycle progress (0–100)
  const billingProgress = useMemo(() => {
    if (!subscription?.startDate || !subscription?.endDate) return 0;
    const start = new Date(subscription.startDate).getTime();
    const end   = new Date(subscription.endDate).getTime();
    const now   = Date.now();
    if (end <= start) return 0;
    return Math.min(100, Math.max(0, Math.round(((now - start) / (end - start)) * 100)));
  }, [subscription]);

  if (!isAuthReady || profileLoading) {
    return (
      <Box sx={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", bgcolor: "#F5F7FA" }}>
        <CircularProgress sx={{ color: ACCENT }} />
      </Box>
    );
  }

  if (!user) return <Navigate to="/login" replace />;

  const handleLogout = async () => { await logout(); navigate("/"); };

  const handleCancelEdit = () => {
    setActiveData({ name: user.name || "", email: user.email || "", phone: user.phone || "", bio: user.bio || "" });
    setEditMode(false);
    setError(null);
  };

  const handleSaveProfile = async () => {
    setSaveMessage(null);
    setError(null);
    if (!activeData.name.trim() || !activeData.email.trim()) { setError("Name and email are required."); return; }
    setSaveLoading(true);
    try {
      const updatedProfile = await authService.updateProfile(user._id, {
        name: activeData.name.trim(),
        email: activeData.email.trim(),
        phone: activeData.phone.trim(),
        bio: activeData.bio.trim(),
      });
      updateUser(updatedProfile);
      setActiveData({ ...activeData, name: updatedProfile.name || activeData.name, email: updatedProfile.email || activeData.email, phone: updatedProfile.phone || "", bio: updatedProfile.bio || "" });
      setEditMode(false);
      setSaveMessage("Profile updated successfully.");
    } catch (err: any) {
      setError(err?.message || "Failed to update profile.");
    } finally {
      setSaveLoading(false);
    }
  };

  const avatarSrc = user?.avatar
    ? user.avatar.startsWith("http") ? user.avatar : `${process.env.REACT_APP_API_URL ?? "http://localhost:5000"}${user.avatar}`
    : undefined;

  const planColor: Record<string, string> = { premium: "#7B1FA2", basic: "#1565C0", starter: "#00897B" };
  const planBg:    Record<string, string> = { premium: "#F3E8FF", basic:   "#EEF4FF", starter: "#E8F5E9" };
  const planKey = subscription?.plan?.toLowerCase() ?? "";
  const referralLink = `${DOMAIN}/signup?ref=${user._id.slice(0, 8)}`;

  return (
    <Box sx={{ bgcolor: "#F5F7FA", minHeight: "100vh", pt: "80px" }}>
      <Helmet>
        <title>My Profile — Dr.Paw</title>
        <meta name="description" content="Manage your Dr.Paw account, subscription, and learning progress." />
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <HreflangTags />

      {/* ── Hero Header ── */}
      <Box sx={{
        background: `linear-gradient(135deg, ${NAVY} 0%, #1A3A5C 100%)`,
        color: "white",
        pt: { xs: 5, md: 8 },
        pb: { xs: 14, md: 18 },
        px: 3,
        position: "relative",
        overflow: "hidden",
      }}>
        {/* Decorative blobs */}
        <Box sx={{ position: "absolute", top: "-20%", right: "-5%", width: 400, height: 400, bgcolor: ACCENT, borderRadius: "50%", opacity: 0.08, filter: "blur(90px)", pointerEvents: "none" }} />
        <Box sx={{ position: "absolute", bottom: "-10%", left: "10%",  width: 300, height: 300, bgcolor: "#42A5F5", borderRadius: "50%", opacity: 0.06, filter: "blur(70px)", pointerEvents: "none" }} />

        <Container maxWidth="lg">
          <Stack direction={{ xs: "column", md: "row" }} spacing={4} alignItems={{ xs: "center", md: "flex-end" }} textAlign={{ xs: "center", md: "left" }}>

            {/* Avatar */}
            <Box sx={{ position: "relative", flexShrink: 0 }}>
              <input ref={fileInputRef} type="file" accept="image/jpeg,image/png,image/webp,image/gif" hidden onChange={handlePhotoChange} />
              <Box sx={{ p: "4px", borderRadius: "50%", background: "linear-gradient(135deg, rgba(255,255,255,0.4), rgba(255,255,255,0.1))", display: "inline-block" }}>
                <Avatar
                  src={avatarSrc}
                  sx={{ width: { xs: 110, md: 150 }, height: { xs: 110, md: 150 }, fontSize: "3.5rem", fontWeight: 700, bgcolor: ACCENT, boxShadow: "0 16px 40px rgba(0,0,0,0.3)" }}
                >
                  {!avatarSrc && (activeData.name?.charAt(0)?.toUpperCase() ?? "?")}
                </Avatar>
              </Box>
              <IconButton
                onClick={() => fileInputRef.current?.click()}
                disabled={photoUploading}
                size="small"
                sx={{ position: "absolute", bottom: 4, right: 4, bgcolor: ACCENT, color: "white", border: "2px solid white", "&:hover": { bgcolor: "#0D47A1" }, boxShadow: "0 4px 12px rgba(0,0,0,0.25)", width: 34, height: 34 }}
              >
                {photoUploading ? <CircularProgress size={14} sx={{ color: "white" }} /> : <CameraIcon sx={{ fontSize: 16 }} />}
              </IconButton>
              {photoError && <Alert severity="error" onClose={() => setPhotoError(null)} sx={{ mt: 1, fontSize: "0.72rem", borderRadius: "8px" }}>{photoError}</Alert>}
            </Box>

            {/* Name / email / chips */}
            <Box sx={{ flexGrow: 1, pb: { md: 1 } }}>
              <Typography variant="h3" sx={{ fontWeight: 800, letterSpacing: "-0.03em", mb: 0.5, fontSize: { xs: "1.8rem", md: "2.4rem" } }}>
                {activeData.name || "My Profile"}
              </Typography>
              <Typography variant="body1" sx={{ opacity: 0.65, mb: 2 }}>{activeData.email}</Typography>
              <Stack direction="row" spacing={1} justifyContent={{ xs: "center", md: "flex-start" }} flexWrap="wrap">
                <Chip
                  label={subscription ? `${subscription.plan.toUpperCase()} MEMBER` : "MEMBER"}
                  size="small"
                  sx={{ bgcolor: "rgba(255,255,255,0.15)", color: "white", fontWeight: 700, backdropFilter: "blur(10px)", borderRadius: "7px", fontSize: "0.72rem" }}
                />
                <Chip
                  label={user.type.toUpperCase()}
                  size="small"
                  icon={<PersonIcon sx={{ fontSize: "14px !important", color: "rgba(255,255,255,0.7) !important" }} />}
                  sx={{ bgcolor: "rgba(255,255,255,0.08)", color: "rgba(255,255,255,0.75)", fontWeight: 600, borderRadius: "7px", fontSize: "0.72rem" }}
                />
              </Stack>
            </Box>

            {/* Logout */}
            <Button
              variant="outlined"
              startIcon={<LogoutIcon />}
              onClick={handleLogout}
              sx={{ borderColor: "rgba(255,255,255,0.3)", color: "white", fontWeight: 700, borderRadius: "12px", px: 3, textTransform: "none", backdropFilter: "blur(10px)", bgcolor: "rgba(255,255,255,0.07)", "&:hover": { bgcolor: "rgba(255,255,255,0.15)", borderColor: "rgba(255,255,255,0.6)" }, flexShrink: 0 }}
            >
              Log Out
            </Button>
          </Stack>
        </Container>
      </Box>

      {/* ── Main Content ── */}
      <Container maxWidth="lg" sx={{ mt: { xs: -8, md: -10 }, position: "relative", zIndex: 10, pb: 10 }}>
        <Grid container spacing={3}>

          {/* ── Stat cards ── */}
          <Grid size={{ xs: 12 }}>
            <Grid container spacing={2}>
              {[
                { icon: <HistoryIcon />,  label: "Purchases",   value: String(purchasesCount),   color: "#1565C0", bg: "#EEF4FF" },
                { icon: <StatsIcon />,    label: "Last Login",  value: formattedLastLogin,        color: "#7B1FA2", bg: "#F3E8FF" },
                { icon: <FavoriteIcon />, label: "Favorites",   value: String(favoritesCount),    color: "#C62828", bg: "#FFEBEE" },
              ].map((s) => (
                <Grid key={s.label} size={{ xs: 12, sm: 4 }}>
                  <Paper elevation={0} sx={{ p: 3, borderRadius: "16px", bgcolor: "white", border: "1px solid rgba(0,0,0,0.05)", display: "flex", alignItems: "center", gap: 2.5, boxShadow: "0 2px 12px rgba(0,0,0,0.04)", transition: "transform 0.2s, box-shadow 0.2s", "&:hover": { transform: "translateY(-3px)", boxShadow: "0 8px 24px rgba(0,0,0,0.08)" } }}>
                    <Box sx={{ width: 52, height: 52, borderRadius: "14px", bgcolor: s.bg, display: "flex", alignItems: "center", justifyContent: "center", color: s.color, flexShrink: 0 }}>
                      {s.icon}
                    </Box>
                    <Box>
                      <Typography variant="h5" sx={{ fontWeight: 800, color: NAVY, lineHeight: 1.2, fontSize: "1.3rem" }}>{s.value}</Typography>
                      <Typography variant="caption" sx={{ color: "#78909C", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.8px", fontSize: "0.65rem" }}>{s.label}</Typography>
                    </Box>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </Grid>

          {/* Alerts */}
          {(error || saveMessage) && (
            <Grid size={{ xs: 12 }}>
              <Stack spacing={1}>
                {error      && <Alert severity="error"   sx={{ borderRadius: "10px" }}>{error}</Alert>}
                {saveMessage && <Alert severity="success" sx={{ borderRadius: "10px" }}>{saveMessage}</Alert>}
              </Stack>
            </Grid>
          )}

          {/* ── Account Settings ── */}
          <Grid size={{ xs: 12, md: 8 }}>
            <Paper elevation={0} sx={{ borderRadius: "20px", border: "1px solid rgba(0,0,0,0.06)", boxShadow: "0 2px 16px rgba(0,0,0,0.04)", overflow: "hidden" }}>
              {/* Card header */}
              <Box sx={{ px: { xs: 3, md: 4 }, py: 2.5, borderBottom: "1px solid rgba(0,0,0,0.06)", bgcolor: "#FAFBFC", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <Typography variant="h6" sx={{ fontWeight: 800, color: NAVY }}>Account Settings</Typography>
                {!editMode ? (
                  <Button startIcon={<EditIcon />} onClick={() => setEditMode(true)} sx={{ textTransform: "none", fontWeight: 700, color: ACCENT, borderRadius: "9px", px: 2, bgcolor: "#EEF4FF", "&:hover": { bgcolor: "#DBEAFE" } }}>
                    Edit Profile
                  </Button>
                ) : (
                  <Stack direction="row" spacing={1}>
                    <Button startIcon={<CloseIcon />} onClick={handleCancelEdit} sx={{ textTransform: "none", fontWeight: 600, color: "#64748B", borderRadius: "9px", px: 2 }}>
                      Cancel
                    </Button>
                    <Button variant="contained" startIcon={saveLoading ? undefined : <SaveOutlined />} onClick={handleSaveProfile} disabled={saveLoading} sx={{ textTransform: "none", fontWeight: 700, borderRadius: "9px", px: 2.5, bgcolor: ACCENT, "&:hover": { bgcolor: "#0D47A1" } }}>
                      {saveLoading ? <CircularProgress size={18} color="inherit" /> : "Save Changes"}
                    </Button>
                  </Stack>
                )}
              </Box>

              {/* Form */}
              <Box sx={{ p: { xs: 3, md: 4 } }}>
                <Grid container spacing={2.5}>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField fullWidth label="Full Name" value={activeData.name} disabled={!editMode} variant="outlined"
                      onChange={(e) => setActiveData({ ...activeData, name: e.target.value })}
                      sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }} />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField fullWidth label="Email Address" value={activeData.email} disabled={!editMode} variant="outlined"
                      onChange={(e) => setActiveData({ ...activeData, email: e.target.value })}
                      sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }} />
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <TextField fullWidth label="Phone Number" value={activeData.phone} disabled={!editMode} variant="outlined"
                      onChange={(e) => setActiveData({ ...activeData, phone: e.target.value })}
                      sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }} />
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <TextField fullWidth multiline rows={3} label="Bio" value={activeData.bio} disabled={!editMode} variant="outlined"
                      onChange={(e) => setActiveData({ ...activeData, bio: e.target.value })}
                      sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }} />
                  </Grid>
                </Grid>
              </Box>

              <Divider />

              {/* Security */}
              <Box sx={{ px: { xs: 3, md: 4 }, py: 3 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 800, color: NAVY, mb: 2 }}>Security</Typography>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ p: 2, borderRadius: "12px", border: "1px solid rgba(0,0,0,0.06)", bgcolor: "#FAFBFC" }}>
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Box sx={{ p: 1.2, bgcolor: "#EEF4FF", borderRadius: "10px" }}>
                      <LockIcon sx={{ color: ACCENT, fontSize: 20 }} />
                    </Box>
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 700, color: NAVY }}>Password</Typography>
                      <Typography variant="caption" sx={{ color: "#78909C" }}>Keep your account secure with a strong password</Typography>
                    </Box>
                  </Stack>
                  <Button sx={{ textTransform: "none", fontWeight: 700, color: ACCENT, borderRadius: "9px", px: 2, bgcolor: "#EEF4FF", "&:hover": { bgcolor: "#DBEAFE" } }}>
                    Change
                  </Button>
                </Stack>
              </Box>
            </Paper>
          </Grid>

          {/* ── Sidebar ── */}
          <Grid size={{ xs: 12, md: 4 }}>
            <Stack spacing={3}>

              {/* Subscription card */}
              <Paper elevation={0} sx={{ borderRadius: "20px", border: "1px solid rgba(0,0,0,0.06)", boxShadow: "0 2px 16px rgba(0,0,0,0.04)", overflow: "hidden" }}>
                <Box sx={{ px: 3, py: 2.5, borderBottom: "1px solid rgba(0,0,0,0.06)", bgcolor: "#FAFBFC", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <Typography variant="h6" sx={{ fontWeight: 800, color: NAVY }}>My Subscription</Typography>
                  {subscription && (
                    <Chip
                      icon={<CheckCircleOutline sx={{ fontSize: "14px !important" }} />}
                      label={subscription.status.charAt(0).toUpperCase() + subscription.status.slice(1)}
                      size="small"
                      sx={{ bgcolor: "#F0FFF4", color: "#276749", fontWeight: 700, fontSize: "0.7rem", borderRadius: "7px", border: "1px solid #C6F6D5" }}
                    />
                  )}
                </Box>

                <Box sx={{ p: 3 }}>
                  {subscription ? (
                    <>
                      {/* Plan badge */}
                      <Box sx={{ p: 2.5, borderRadius: "14px", bgcolor: planBg[planKey] || "#EEF4FF", border: `1px solid ${planColor[planKey] || ACCENT}22`, mb: 3 }}>
                        <Typography variant="caption" sx={{ color: planColor[planKey] || ACCENT, fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.8px", fontSize: "0.65rem" }}>
                          {subscription.plan.toUpperCase()} PLAN
                        </Typography>
                        <Stack direction="row" alignItems="baseline" spacing={0.5} sx={{ mt: 0.5, mb: 1.5 }}>
                          <Typography variant="h4" sx={{ fontWeight: 800, color: NAVY, lineHeight: 1 }}>{subscriptionPrice}</Typography>
                          <Typography variant="body2" sx={{ color: "#78909C" }}>/{subscription.billingCycle}</Typography>
                        </Stack>

                        {/* Billing progress */}
                        <LinearProgress
                          variant="determinate"
                          value={billingProgress}
                          sx={{ height: 6, borderRadius: 99, bgcolor: "rgba(0,0,0,0.06)", "& .MuiLinearProgress-bar": { bgcolor: planColor[planKey] || ACCENT, borderRadius: 99 } }}
                        />
                        <Stack direction="row" justifyContent="space-between" sx={{ mt: 1 }}>
                          <Typography variant="caption" sx={{ color: "#78909C", fontSize: "0.68rem" }}>Billing cycle</Typography>
                          <Typography variant="caption" sx={{ color: "#78909C", fontSize: "0.68rem" }}>{billingProgress}% used</Typography>
                        </Stack>
                      </Box>

                      {/* Next billing */}
                      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 3, p: 1.5, borderRadius: "10px", bgcolor: "#F8FAFC", border: "1px solid rgba(0,0,0,0.05)" }}>
                        <CalendarMonth sx={{ color: "#78909C", fontSize: 18 }} />
                        <Box>
                          <Typography variant="caption" sx={{ color: "#78909C", fontSize: "0.68rem" }}>Next billing date</Typography>
                          <Typography variant="body2" sx={{ fontWeight: 700, color: NAVY }}>{nextBillingDate}</Typography>
                        </Box>
                      </Stack>

                      <Button fullWidth variant="outlined" sx={{ borderRadius: "10px", textTransform: "none", fontWeight: 700, color: ACCENT, borderColor: "rgba(21,101,192,0.3)", "&:hover": { bgcolor: "#EEF4FF", borderColor: ACCENT } }}>
                        Manage Subscription
                      </Button>
                    </>
                  ) : (
                    <Box sx={{ textAlign: "center", py: 3 }}>
                      <Typography variant="body2" sx={{ color: "#78909C", mb: 2 }}>No active subscription</Typography>
                      <Button variant="contained" sx={{ borderRadius: "10px", textTransform: "none", fontWeight: 700, bgcolor: ACCENT, "&:hover": { bgcolor: "#0D47A1" } }} onClick={() => navigate("/pricing")}>
                        View Plans
                      </Button>
                    </Box>
                  )}
                </Box>
              </Paper>

              {/* Payment Method card */}
              <Paper elevation={0} sx={{ borderRadius: "20px", border: "1px solid rgba(0,0,0,0.06)", boxShadow: "0 2px 16px rgba(0,0,0,0.04)", overflow: "hidden" }}>
                <Box sx={{ px: 3, py: 2.5, borderBottom: "1px solid rgba(0,0,0,0.06)", bgcolor: "#FAFBFC" }}>
                  <Typography variant="h6" sx={{ fontWeight: 800, color: NAVY }}>Payment Method</Typography>
                </Box>
                <Box sx={{ p: 3 }}>
                  <Stack direction="row" spacing={2} alignItems="center" sx={{ p: 2, borderRadius: "12px", border: "1px solid rgba(0,0,0,0.06)", bgcolor: "#FAFBFC" }}>
                    <Box sx={{ p: 1.2, bgcolor: "#EEF4FF", borderRadius: "10px", flexShrink: 0 }}>
                      <PaymentIcon sx={{ color: ACCENT, fontSize: 20 }} />
                    </Box>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="body2" sx={{ fontWeight: 700, color: NAVY, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {subscription?.paymentMethod || "No payment method on file"}
                      </Typography>
                      <Typography variant="caption" sx={{ color: "#78909C" }}>
                        {subscription ? `Status: ${subscription.status}` : "Add a payment method from checkout"}
                      </Typography>
                    </Box>
                  </Stack>
                </Box>
              </Paper>

              {/* Invite Friends card */}
              <Paper elevation={0} sx={{ borderRadius: "20px", border: "1px solid rgba(0,0,0,0.06)", boxShadow: "0 2px 16px rgba(0,0,0,0.04)", overflow: "hidden" }}>
                <Box sx={{ px: 3, py: 2.5, borderBottom: "1px solid rgba(0,0,0,0.06)", bgcolor: "#FAFBFC" }}>
                  <Typography variant="h6" sx={{ fontWeight: 800, color: NAVY }}>Invite Friends</Typography>
                </Box>
                <Box sx={{ p: 3 }}>
                  <Typography variant="body2" sx={{ color: "#78909C", mb: 2 }}>
                    Share your link — when a friend signs up, you both win.
                  </Typography>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ p: 1.5, borderRadius: "10px", border: "1px solid rgba(0,0,0,0.06)", bgcolor: "#FAFBFC" }}>
                    <Typography variant="body2" sx={{ color: NAVY, flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontWeight: 600 }}>
                      {referralLink}
                    </Typography>
                    <CopyButton text={referralLink} small />
                  </Stack>
                </Box>
              </Paper>

            </Stack>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default Profile;
